/*
 * Product gallery slide hydration (perf round 2, finding C1).
 *
 * Image slides after the first ship their sources as data-src / data-srcset
 * (snippets/product-thumbnail.liquid and the block-image / record slides in
 * snippets/product-media-gallery.liquid). `loading="lazy"` alone did not keep
 * them out of the LCP window: the next slide sits inside Chrome's lazy-load
 * distance, so on Slow 4G slides 2 and 3 (109 KB + a 218 KB GIF) started at
 * 4.2 s while the LCP image was still downloading until 9.2 s.
 *
 * Sources come back on whichever happens FIRST:
 *   1. window `load` -> every deferred slide of every gallery.
 *   2. The eager first slide's own `<img>` firing `load` (or already
 *      `complete` with a real `naturalWidth` when this module registers the
 *      gallery, e.g. it was served from the disk cache) -> its next
 *      displayed neighbour, even before window `load`. By the time that
 *      image has arrived the LCP image is already in, so this hydration
 *      does not compete with it; it just closes the window where a quick
 *      early swipe still finds a blank slide 2. `complete` alone is not
 *      enough: it is also true for a broken image (404, cached network
 *      failure), so a `naturalWidth` of 0 -- and an `error` instead of
 *      `load` -- is left alone here and falls through to the other triggers.
 *   3. Before load, a slide that is really on screen (IntersectionObserver on
 *      the viewport, no rootMargin) -> that slide. A slide showing less than
 *      half its width does not count: that is the next slide touching the
 *      edge of the mobile slider, exactly the request this file exists to
 *      hold back. Desktop stacked / columns / masonry slides in the viewport
 *      are fully visible across and hydrate straight away.
 *   4. Gallery interaction before load -> the slide concerned and its two
 *      neighbours: touchstart / pointerdown / horizontal wheel on a slide,
 *      keyboard focus inside a slide, the gallery slider's prev/next buttons,
 *      a gallery dot, a thumbnail (pointerdown, click or focus), and the
 *      slider's `slideChanged` (any scroll that moves it to another slide).
 *   5. media-gallery.js setActiveMedia (variant change, thumbnail, bundle
 *      picker, loop widget) and its tier-image swap -> the activated slide and
 *      its neighbours; after load, the whole gallery (slides a variant change
 *      inserted late included).
 *
 * Hydrated images keep loading="lazy", so the browser still decides when an
 * off-screen slide actually downloads. A non-empty source that someone else
 * wrote first (a variant or tier image swap) wins: hydration then only drops
 * the data-* attributes. An empty src/srcset does not count. The theme editor renders real sources, so nothing happens there.
 *
 * Re-executed by HTMLUpdateUtility.setInnerHTML (quick add, product swap):
 * the second run only registers the new galleries.
 */
(() => {
  'use strict';

  if (window.productMediaHydrate) {
    window.productMediaHydrate.scan();
    return;
  }

  const SLIDE = 'li[data-media-id]';
  const DEFERRED = 'img[data-src], img[data-srcset]';
  const GALLERY_DEFERRED = `media-gallery ${SLIDE} img[data-src], media-gallery ${SLIDE} img[data-srcset]`;
  const NAV_TARGET = '.gallery-dot[data-target], .thumbnail-list__item[data-target]';
  const INTERACTIONS = ['pointerdown', 'touchstart', 'wheel', 'click', 'focusin'];

  const registered = new WeakSet();
  const observers = [];
  let loaded = document.readyState === 'complete';

  function hydrateImage(img) {
    const src = img.getAttribute('data-src');
    const srcset = img.getAttribute('data-srcset');
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
    // A non-empty source someone else wrote first wins. An empty one is not a
    // source: media-gallery.js announceLiveRegion (image.src = image.src)
    // leaves src="" on a slide it reaches before this module.
    if (img.getAttribute('src') || img.getAttribute('srcset')) return;
    // srcset before src, so the browser never starts on the 1946 px fallback.
    if (srcset) img.setAttribute('srcset', srcset);
    if (src) img.setAttribute('src', src);
  }

  function hydrateSlide(slide) {
    if (!slide) return;
    slide.querySelectorAll(DEFERRED).forEach(hydrateImage);
  }

  // Scoped to gallery slides: other sections use data-src for their own
  // loaders. querySelectorAll matches the selector against the whole document,
  // so a media-gallery root still satisfies the `media-gallery` ancestor.
  function hydrateAll(root = document) {
    root.querySelectorAll(GALLERY_DEFERRED).forEach(hydrateImage);
  }

  function slidesOf(gallery) {
    return Array.from(gallery.querySelectorAll(SLIDE));
  }

  function isShown(slide) {
    return window.getComputedStyle(slide).display !== 'none';
  }

  function hydrateAround(slide) {
    if (!slide) return;
    const gallery = slide.closest('media-gallery');
    if (loaded && gallery) {
      hydrateAll(gallery);
      return;
    }
    hydrateSlide(slide);
    if (!gallery) return;
    const slides = slidesOf(gallery).filter((candidate) => candidate === slide || isShown(candidate));
    const index = slides.indexOf(slide);
    hydrateSlide(slides[index - 1]);
    hydrateSlide(slides[index + 1]);
  }

  function viewerOf(gallery) {
    return gallery.querySelector('[id^="GalleryViewer"]') || gallery.querySelector('slider-component');
  }

  function slideById(gallery, mediaId) {
    return slidesOf(gallery).find((slide) => slide.dataset.mediaId === mediaId) || null;
  }

  function currentSlide(gallery) {
    const viewer = viewerOf(gallery);
    const items = viewer && viewer.sliderItemsToShow;
    const fromSlider = items && viewer.currentPage ? items[viewer.currentPage - 1] : null;
    return fromSlider || gallery.querySelector(`${SLIDE}.is-active`) || gallery.querySelector(SLIDE);
  }

  function onInteraction(gallery, event) {
    if (loaded) return;
    if (event.type === 'wheel' && Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    const element = event.target instanceof Element ? event.target : null;
    if (!element) return;

    const navTarget = element.closest(NAV_TARGET);
    if (navTarget && gallery.contains(navTarget)) {
      hydrateAround(slideById(gallery, navTarget.dataset.target));
      return;
    }

    const slide = element.closest(SLIDE);
    if (slide && gallery.contains(slide)) {
      hydrateAround(slide);
      return;
    }

    const button = element.closest('button[name="previous"], button[name="next"]');
    const viewer = viewerOf(gallery);
    if (button && viewer && viewer.contains(button)) hydrateAround(currentSlide(gallery));
  }

  function isOnScreen(entry) {
    const visible = entry.intersectionRect;
    if (!entry.isIntersecting || visible.width <= 0 || visible.height <= 0) return false;
    return visible.width * 2 >= entry.boundingClientRect.width;
  }

  // The eager first slide's own image has no data-src/data-srcset (it ships
  // real sources), so hydrateSlide(first) is a no-op; hydrateAround still
  // hydrates its next displayed neighbour (index + 1 of the visible list),
  // which is the point of this trigger. `complete` is true both for a
  // successfully loaded image AND a broken one (404, cached network
  // failure), so it is only trusted alongside `naturalWidth > 0` -- otherwise
  // a first slide whose image errored would "hydrate" slide 2 as though the
  // LCP image had already arrived, when it never did. A first image that
  // errors instead of loading (whether already failed at registration, or it
  // fails afterward) is left alone here: it falls through to the existing
  // window `load` / interaction / IntersectionObserver triggers, same as any
  // other gallery.
  function watchFirstSlide(gallery) {
    const first = slidesOf(gallery)[0];
    const firstImg = first && first.querySelector('img');
    if (!firstImg) return;
    if (firstImg.complete) {
      if (firstImg.naturalWidth > 0) hydrateAround(first);
      return;
    }
    const onFirstLoad = () => hydrateAround(first);
    firstImg.addEventListener('load', onFirstLoad, { once: true });
    firstImg.addEventListener('error', () => firstImg.removeEventListener('load', onFirstLoad), { once: true });
  }

  function observe(gallery) {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!isOnScreen(entry)) return;
          hydrateSlide(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    slidesOf(gallery).forEach((slide) => {
      if (slide.querySelector(DEFERRED)) observer.observe(slide);
    });
    observers.push(observer);
  }

  function register(gallery) {
    if (!gallery || registered.has(gallery)) return;
    registered.add(gallery);
    if (loaded) {
      hydrateAll(gallery);
      return;
    }

    const handler = (event) => onInteraction(gallery, event);
    INTERACTIONS.forEach((type) => gallery.addEventListener(type, handler, { capture: true, passive: true }));

    const viewer = viewerOf(gallery);
    if (viewer) {
      viewer.addEventListener('slideChanged', (event) => {
        if (!loaded) hydrateAround(event.detail && event.detail.currentElement);
      });
    }

    observe(gallery);
    watchFirstSlide(gallery);
  }

  function scan() {
    document.querySelectorAll('media-gallery').forEach(register);
  }

  function onLoad() {
    loaded = true;
    observers.splice(0).forEach((observer) => observer.disconnect());
    hydrateAll(document);
  }

  window.productMediaHydrate = {
    register,
    scan,
    hydrateSlide,
    hydrateAround,
    hydrateAll,
    get loaded() {
      return loaded;
    },
  };

  if (!loaded) window.addEventListener('load', onLoad, { once: true });
  scan();
})();
